import React, { useState } from "react";
import ModalTemplate from "../../components/templates/ModalTemplate";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  PaymentType,
  ProposalType,
  SettingType,
} from "../../app/types/ProposalTypes";
import ConfirmationModal from "./ConfirmationModal";
import Button from "../../components/atoms/Button";

function SignModal({
  openSignModal,
  handleCloseModal,
  proposalSettings,
  signature,
  setSignature,
  handleSendSignature,
  onBehalfChecked,
  setOnBehalfChecked,
  fontClass,
  data,
  stylingType = "events_styling",
  company,
}: {
  openSignModal: boolean;
  handleCloseModal: () => void;
  proposalSettings: {
    proposal: ProposalType;
    settings: SettingType;
  };
  data: PaymentType[] | null;
  stylingType?: "events_styling" | "booking_proposal_styling";
  signature: string;
  setSignature: (value: string) => void;
  handleSendSignature: () => void;
  fontClass?: string;
  onBehalfChecked: boolean;
  setOnBehalfChecked: (value: boolean) => void;
  company?: any;
}) {
  const hadPayment = proposalSettings.proposal.payment;
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const handleCancelClick = () => {
    setShowConfirmationModal(true);
  };

  return (
    <>
      <ModalTemplate
        open={openSignModal}
        handleClose={handleCloseModal}
        className={`w-full max-w-2xl ${fontClass || "font-roboto"}`}
      >
        <div className="bg-white">
          <div className="p-4 flex flex-row justify-between items-center bg-[#FAFAFA] sticky top-0 z-10">
            <span className={`font-semibold text-xl`}>
              {globalThis.translate.em.proposal.signature_page.sign_modal.title}
            </span>
            <IconButton onClick={handleCloseModal} className="h-8 w-8">
              <CloseIcon />
            </IconButton>
          </div>
          <div className="p-8">
            <div className={`mb-6 font-normal text-lg text-[#2D2020]`}>
              {
                globalThis.translate.em.proposal.signature_page.sign_modal
                  .terms_title
              }
            </div>
            <div className="p-2 bg-[#FAF7F6]">
              <div className={`mb-4 text-[#99472A] text-lg font-medium`}>
                {
                  globalThis.translate.em.proposal.signature_page.sign_modal
                    .terms_subtitle
                }
              </div>
              {/* Descriptive text */}
              <div className="mb-6 space-y-2 text-[13px]  text-[#2D2020]">
                <span
                  className={`text-[13px] font-normal`}
                  dangerouslySetInnerHTML={{
                    __html: proposalSettings.settings.terms_conditions,
                  }}
                />
              </div>
            </div>
          </div>

          {/* Signature section */}
          <div className="mb-6 p-6">
            <p className={`text-sm font-medium mb-4`}>
              {
                globalThis.translate.em.proposal.signature_page.sign_modal
                  .signature_section_title
              }
            </p>

            <div className="p-4">
              <div className="flex flex-row gap-4 items-center mb-2">
                <div>
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 36 35"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M34.6668 18.5565C28.0002 24.4099 27.0468 15.2015 23.5835 15.8615C19.6668 16.6065 17.1668 24.9082 19.6668 24.9082C22.1668 24.9082 18.8335 14.9999 15.5002 18.4249C12.1668 21.8499 11.1002 26.3249 8.39183 23.0049C-4.49984 7.1882 6.3335 -4.41681 11.6052 3.26153C14.9418 8.12153 8.8335 25.7949 1.3335 34.1665M13.0002 32.4999H29.6668"
                      stroke="#666"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <input
                  className={`text-lg font-normal h-full w-full outline-none bg-inherit text-gray-800`}
                  placeholder={
                    globalThis.translate.em.proposal.signature_page.sign_modal
                      .signature_input_placeholder
                  }
                  value={signature}
                  onChange={(event) => setSignature(event.target.value)}
                />
              </div>

              <div className="border-b border-gray-400 mb-2"></div>

              {company != null && company !== undefined && (
                <div className="flex items-center gap-2 mb-2 mt-4">
                  <input
                    type="checkbox"
                    checked={onBehalfChecked}
                    onChange={(event) =>
                      setOnBehalfChecked(event.target.checked)
                    }
                  />
                  <span>
                    {globalThis.translate.em.proposal.signature_page.on_behalf}
                  </span>
                </div>
              )}

              {/* Display signature in script font */}
              <div className="text-6xl italic font-dancing font-bold text-gray-800 font-script py-2 flex justify-center min-h-[120px]">
                {signature || ""}
              </div>
            </div>

            <p className={`text-lg font-normal text-[#99472A] mt-2 `}>
              {
                globalThis.translate.em.proposal.signature_page.sign_modal
                  .signature_under
              }
            </p>
          </div>

          <div className="bg-[#FAFAFA] flex gap-1 rounded sticky bottom-0 mt-6">
            <div className="flex flex-row justify-end items-center w-full gap-3 px-6 py-4">
              <button
                onClick={handleCancelClick}
                className={`py-2 px-6 rounded font-medium text-gray-700 hover:bg-gray-50 `}
              >
                {
                  globalThis.translate.em.proposal.signature_page.sign_modal
                    .cancel
                }
              </button>
              <Button
                onClick={handleSendSignature}
                className={`bg-[#BF5934] py-2 px-6 rounded text-white font-medium hover:bg-[#A04426] disabled:bg-gray-400 `}
                disabled={!signature.trim()}
                stylingType={stylingType}
              >
                {hadPayment
                  ? globalThis.translate.em.proposal.signature_page.sign_modal
                      .sign_and_pay
                  : globalThis.translate.em.proposal.signature_page.sign_modal
                      .sign_confirm}
              </Button>
            </div>
          </div>
        </div>
      </ModalTemplate>

      <ConfirmationModal
        data={data}
        handleClose={handleCloseModal}
        showConfirmationModal={showConfirmationModal}
        setShowConfirmationModal={setShowConfirmationModal}
        fontClass={fontClass}
        stylingType={stylingType}
      />
    </>
  );
}

export default SignModal;
